import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Paper,
} from '@mui/material';
import {
  Code,
  Security,
  LocalShipping,
  CloudUpload,
  Speed,
  Support,
  ArrowForward,
  CheckCircle,
  Business,
  AccountBalance,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Custom Software Development',
      icon: <Code sx={{ fontSize: 40 }} />,
      description: 'Enterprise-grade applications tailored to government and commercial requirements with security-first architecture.',
      features: ['Legacy Modernization', 'API Development', 'Cloud Migration', 'Agile Delivery'],
      path: '/services/custom-software',
      color: '#1e3a5f',
    },
    {
      title: 'Managed IT Services',
      icon: <Security sx={{ fontSize: 40 }} />,
      description: '24/7 infrastructure monitoring and management with enterprise-level support and cybersecurity protection.',
      features: ['24/7 Monitoring', 'Cybersecurity', 'Help Desk', 'Disaster Recovery'],
      path: '/services/managed-it',
      color: '#00bcd4',
    },
    {
      title: 'Logistics Consulting',
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      description: 'Supply chain optimization and distribution strategies that reduce costs and improve operational efficiency.',
      features: ['Supply Chain Analytics', 'Route Optimization', 'Inventory Management', 'Cost Reduction'],
      path: '/services/logistics',
      color: '#4caf50',
    },
  ];

  // const stats = [
  //   { value: '0+', label: 'Projects Delivered' },
  //   { value: '0%', label: 'Client Satisfaction' },
  //   { value: '24/7', label: 'Support Available' },
  //   { value: '0+', label: 'Government Contracts' },
  // ];

  // const partners = [
  //  '',
  // ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(/home_page.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          pt: 12,
          pb: 10,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(30, 58, 143, 0.4)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                }}
              >
                Transforming Supply Chain & Enterprise IT
              </Typography>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.95 }}>
                Certified government contractor specializing in custom software development,
                managed IT services, and logistics optimization.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap' }}>
                <Chip
                  label="FedRAMP"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip
                  label="SOC"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip
                  label="NIST"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip
                  label="ISO"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/contact')}
                  sx={{ py: 1.5, px: 3 }}
                >
                  Schedule Consultation
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/CapabilityStatement.pdf"
                  download="CapabilityStatement.pdf"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  Download Capability Statement
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              {/* Image is now the background */}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      {/* <Box sx={{ bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {stats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" fontWeight={700}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Services Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" gutterBottom>
              Our Core Services
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Comprehensive technology solutions for government and enterprise
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {services.map((service) => (
              <Grid item xs={12} md={4} key={service.title}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 6,
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom fontWeight={600}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {service.description}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {service.features.map((feature) => (
                        <Box 
                          key={feature}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <CheckCircle 
                            sx={{ 
                              fontSize: 18, 
                              mr: 1, 
                              color: 'success.main' 
                            }} 
                          />
                          <Typography variant="body2">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button 
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(service.path)}
                      endIcon={<ArrowForward />}
                    >
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" gutterBottom>
              Why Government Agencies Choose Us
            </Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Cloud-First Approach
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AWS, Azure, and Google Cloud certified experts delivering scalable, 
                  secure cloud solutions that meet government compliance standards.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Speed sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Agile Delivery
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Proven agile methodology ensuring rapid deployment, continuous improvement, 
                  and transparent communication throughout the project lifecycle.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Support sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  24/7 Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Round-the-clock monitoring and support with guaranteed SLAs, ensuring 
                  mission-critical systems remain operational at all times.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Partners Section */}
      {/* <Box sx={{ bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Trusted Technology Partners
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
              {partners.map((partner) => (
                <Chip
                  key={partner}
                  label={partner}
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: '0.9rem',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box> */}

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Ready to Transform Your IT Infrastructure?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Let's discuss how Intellusia Studios can help your organization achieve its technology goals.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: 'white',
                color: 'primary.main',
                py: 1.5,
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}
              onClick={() => navigate('/contact')}
            >
              Schedule Free Consultation
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ 
                color: 'white',
                borderColor: 'white',
                py: 1.5,
                px: 4,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              View Case Studies
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;